import type {Options as VectorSourceOptions} from 'ol/source/Vector';
import VectorSource from 'ol/source/Vector.js';
import GeoJSON from 'ol/format/GeoJSON.js';
import Style from 'ol/style/Style.js';
import Stroke from 'ol/style/Stroke.js';
import Icon from 'ol/style/Icon.js';
import VectorLayer from 'ol/layer/Vector.js';
import type {Options as VectorLayerOptions} from 'ol/layer/Vector';
import {Z_INDEXES} from '../constants.ts';
import type Feature from 'ol/Feature';
import {BASE_API_URL} from '../../envs.ts';

const OUTLINE_COLOR = [255, 51, 0];
const INNERLINE_COLOR = [255, 102, 0];

const SLOWUP_LINE_STYLES: {[key: string]: Style[]} = {
  pro_skate: [
    new Style({
      stroke: new Stroke({
        color: OUTLINE_COLOR,
        width: 10,
        lineDash: [0, 13],
      }),
    }),
    new Style({
      stroke: new Stroke({
        color: INNERLINE_COLOR,
        width: 8,
        lineDash: [0, 13],
      }),
    }),
  ],
  nature: [
    new Style({
      stroke: new Stroke({
        color: OUTLINE_COLOR,
        width: 9,
        lineCap: 'butt',
        lineDash: [20, 15],
      }),
    }),
    new Style({
      stroke: new Stroke({
        color: INNERLINE_COLOR,
        width: 7,
        lineCap: 'butt',
        lineDash: [20, 15],
      }),
    }),
  ],
};

const getSlowupMinResolutionForFeature: (feature: Feature) => number | null = (
  feature,
) => {
  const categorySlowup = feature.get('category_slowup');
  if (categorySlowup) {
    if (
      categorySlowup === 'fairground' ||
      categorySlowup === 'direction_arrow'
    ) {
      return 50;
    }
    return 10;
  }
  return null;
};

const createPlainLineStyle: (
  outerWidth: number,
  innerWidth: number,
) => Style[] = (outerWidth: number, innerWidth: number) => {
  return [
    new Style({
      stroke: new Stroke({
        color: OUTLINE_COLOR,
        width: outerWidth,
      }),
    }),
    new Style({
      stroke: new Stroke({
        color: INNERLINE_COLOR,
        width: innerWidth,
      }),
    }),
  ];
};

const lowResolutionLineStyle: Style[] = createPlainLineStyle(6, 4);
const highResolutionLineStyle: Style[] = createPlainLineStyle(8, 6);

export class SlowupSource extends VectorSource {
  constructor(options?: VectorSourceOptions<Feature>) {
    super(
      Object.assign(
        {},
        {
          url: `${BASE_API_URL}/api/4/slowups.geojson?srid=21781`,
          format: new GeoJSON({
            dataProjection: 'EPSG:21781',
          }),
          properties: {name: 'slowup'},
        },
        options,
      ),
    );
    this.on('addfeature', (evt) => {
      const f: Feature = evt.feature;
      const minResolution = getSlowupMinResolutionForFeature(f);
      if (minResolution) {
        f.set('resolution', minResolution, true);
      }
    });
  }
}

export class SlowupLayer extends VectorLayer<VectorSource> {
  private poiStylesCache: {[key: string]: Style} = {};

  constructor(options?: VectorLayerOptions) {
    super(
      Object.assign(
        {},
        {
          properties: {layername: 'slowup', name: 'slowup'},
          visible: false,
          source: new SlowupSource(),
          zIndex: Z_INDEXES.SLOWUP,
          style: (feature: Feature, resolution: number) => {
            const categorySurface = feature.get('category_surface');
            if (categorySurface) {
              if (resolution > 20) {
                return lowResolutionLineStyle;
              }
              return (
                SLOWUP_LINE_STYLES[categorySurface] || highResolutionLineStyle
              );
            }

            if (resolution < 50) {
              const categorySlowup = feature.get('category_slowup');
              if (categorySlowup) {
                if (
                  resolution > 10 &&
                  categorySlowup !== 'fairground' &&
                  categorySlowup !== 'direction_arrow'
                ) {
                  // faireground and direction arrows are displayed when resolution < 50
                  // the others when resolution < 10
                  return null;
                }
                const azimut = feature.get('azimut');
                const style = this.getPOIStyle(categorySlowup);
                const rotation =
                  (((360 - (azimut || 0)) % 360) * Math.PI) / 180;
                style.getImage().setRotation(rotation);
                const rotateWithView = azimut !== undefined;
                style.getImage().setRotateWithView(rotateWithView);
                return style;
              }
            }
            return null;
          },
        },
        options,
      ),
    );
  }

  private getPOIStyle(name: string) {
    let style = this.poiStylesCache[name];
    if (!style) {
      style = this.poiStylesCache[name] = new Style({
        image: new Icon({
          rotateWithView: true,
          crossOrigin: 'anonymous',
          size: [22, 22],
          src: `/img/slowup/${name}.svg?ts11=TIMESTAMP11`,
        }),
      });
    }
    return style;
  }
}
